import {
  compositeKey,
  targetKey,
  type FlagOverride,
  type FlagTarget,
  type OverrideScope,
} from "~/features/feature-flags/shared/contracts"
import {
  FEATURE_FLAG_KEYS,
  FEATURE_FLAGS,
  type FeatureFlagKey,
  type FlagEnvironment,
} from "~/features/feature-flags/shared/registry"

export type FlagValueSource = OverrideScope | "code"

export type ResolvedFlag = {
  value: boolean
  /** Where the flag's own value came from, before prerequisites are applied. */
  source: FlagValueSource
  /** The first prerequisite that is off, whatever the flag's own value. */
  blockedBy: string | null
}

export type FlagSnapshot = {
  /** Every flag resolved for one company (and optionally one user). Memoized. */
  resolve: (
    orgId: string | null | undefined,
    userId?: string | null,
  ) => Record<FeatureFlagKey, ResolvedFlag>
  /** `resolve`'s values alone. */
  values: (
    orgId: string | null | undefined,
    userId?: string | null,
  ) => Record<FeatureFlagKey, boolean>
  /** The stored setting for a target: its override value, or null when it follows the default. */
  override: (target: FlagTarget) => boolean | null
  /** The default a flag has: its `default` override, else the registry's for the environment. */
  defaultValue: (flagKey: FeatureFlagKey) => boolean
}

/** One environment's overrides, resolved by the one evaluator client and server share. Each company
 * and user is resolved once per snapshot. */
export function createFlagSnapshot(
  overrides: readonly FlagOverride[],
  environment: FlagEnvironment,
): FlagSnapshot {
  const byTarget = new Map(overrides.map((o) => [targetKey(o), o.value] as const))

  const defaultSetting = (flagKey: FeatureFlagKey): { value: boolean; source: FlagValueSource } => {
    const stored = byTarget.get(targetKey({ flagKey, scope: "default", targetId: "" }))
    return stored === undefined
      ? { value: FEATURE_FLAGS[flagKey].defaults[environment], source: "code" }
      : { value: stored, source: "default" }
  }

  const ownSetting = (flagKey: FeatureFlagKey, orgId: string | null, userId: string | null) => {
    const stored = (scope: OverrideScope, targetId: string) =>
      byTarget.get(targetKey({ flagKey, scope, targetId }))
    const user = userId ? stored("user", userId) : undefined
    if (user !== undefined) return { value: user, source: "user" as const }
    const org = orgId ? stored("org", orgId) : undefined
    if (org !== undefined) return { value: org, source: "org" as const }
    return defaultSetting(flagKey)
  }

  /** Precedence: user override, company override, default override, code default. A flag with a
   * prerequisite that resolves off is off, whatever its own value. */
  const evaluate = (orgId: string | null, userId: string | null) => {
    const resolved = new Map<FeatureFlagKey, ResolvedFlag>()
    const resolveFlag = (key: FeatureFlagKey): ResolvedFlag => {
      const cached = resolved.get(key)
      if (cached) return cached
      const own = ownSetting(key, orgId, userId)
      const blockedBy =
        (FEATURE_FLAGS[key].requires ?? []).find(
          (prerequisite) => !resolveFlag(prerequisite).value,
        ) ?? null
      const flag = { value: own.value && blockedBy === null, source: own.source, blockedBy }
      resolved.set(key, flag)
      return flag
    }
    return Object.fromEntries(FEATURE_FLAG_KEYS.map((key) => [key, resolveFlag(key)])) as Record<
      FeatureFlagKey,
      ResolvedFlag
    >
  }

  const resolvedCache = new Map<string, Record<FeatureFlagKey, ResolvedFlag>>()
  const resolve: FlagSnapshot["resolve"] = (orgId, userId) => {
    const key = compositeKey(orgId ?? "", userId ?? "")
    let flags = resolvedCache.get(key)
    if (!flags) {
      flags = evaluate(orgId || null, userId || null)
      resolvedCache.set(key, flags)
    }
    return flags
  }

  return {
    resolve,
    values: (orgId, userId) => {
      const flags = resolve(orgId, userId)
      return Object.fromEntries(FEATURE_FLAG_KEYS.map((key) => [key, flags[key].value])) as Record<
        FeatureFlagKey,
        boolean
      >
    },
    override: (target) => byTarget.get(targetKey(target)) ?? null,
    defaultValue: (flagKey) => defaultSetting(flagKey).value,
  }
}

/** Every flag: its given value, else the environment's registry default. Version skew or stale
 * last-known flags can omit keys, and prerequisites must still hold over the result. */
export const completeFlags = (
  given: Partial<Record<FeatureFlagKey, boolean>>,
  environment: FlagEnvironment,
): Record<FeatureFlagKey, boolean> =>
  createFlagSnapshot(
    FEATURE_FLAG_KEYS.flatMap((flagKey) => {
      const value = given[flagKey]
      return value === undefined
        ? []
        : [{ flagKey, scope: "default" as const, targetId: "", value }]
    }),
    environment,
  ).values(null)
